import { ListOption } from '@zendesk/forethought-elements';
import {
  FieldPredictorConfig,
  FreshdeskTicketField,
  FreshdeskWrittenObject,
  JiraTicketField,
  JiraWrittenObject,
  KustomerTicketField,
  KustomerWrittenObject,
  TriageHelpdeskTicketFields,
  TriageKustomerTicketFields,
  TriageSalesforceTicketFields,
  ZendeskTicketField,
  ZendeskWrittenObject,
} from './types';
import { Helpdesk } from 'src/components/app/types';

export const isSalesforceTicketFields = (
  ticketFields: TriageHelpdeskTicketFields | undefined,
): ticketFields is TriageSalesforceTicketFields => {
  return (
    (ticketFields as TriageSalesforceTicketFields)?.case_origins !== undefined
  );
};

export const isKustomerTicketFields = (
  ticketFields: TriageHelpdeskTicketFields | undefined,
): ticketFields is TriageKustomerTicketFields => {
  return (ticketFields as TriageKustomerTicketFields) !== undefined;
};

export const getFieldPredictorOutputField = (
  helpdesk: Helpdesk | undefined | null,
  fieldsToPredict: FieldPredictorConfig,
): Record<string, string> | undefined => {
  switch (helpdesk) {
    case 'zendesk': {
      const writtenObject = fieldsToPredict?.outputs[0]?.written_object;
      const zendeskWrittenObject =
        writtenObject as unknown as ZendeskWrittenObject;
      // Custom fields are nested under the custom_fields key
      if (zendeskWrittenObject?.custom_fields) {
        return zendeskWrittenObject.custom_fields;
      }
      // System fields (status, priority, brand_id, etc.) are stored at the top level
      if (writtenObject && typeof writtenObject === 'object') {
        return writtenObject as unknown as Record<string, string>;
      }
      return undefined;
    }
    case 'salesforce':
      return fieldsToPredict?.outputs[0]?.written_object as Record<
        string,
        string
      >;
    case 'kustomer':
      const kustomerWrittenObject = fieldsToPredict?.outputs[0]
        ?.written_object as unknown as KustomerWrittenObject;
      return kustomerWrittenObject?.attributes?.custom;
    case 'jira': {
      const jiraWrittenObject = fieldsToPredict?.outputs[0]
        ?.written_object as unknown as JiraWrittenObject;
      const customFields = jiraWrittenObject?.fields;
      if (customFields && typeof customFields === 'object') {
        const [key, value] = Object.entries(customFields)[0] || [];
        return key && value ? { [key]: value as string } : undefined;
      }
      return undefined;
    }
    case 'freshdesk':
      const freshdeskWrittenObject = fieldsToPredict?.outputs[0]
        ?.written_object as unknown as FreshdeskWrittenObject;
      return freshdeskWrittenObject?.custom_field;
    case 'popx_servicenow':
      return fieldsToPredict?.outputs[0]?.written_object as Record<
        string,
        string
      >;
    default:
      return;
  }
};

export const getDataSourcePredictedField = (
  fieldToPredictOutputFieldId: string,
  ticketFields: TriageHelpdeskTicketFields | undefined,
): ListOption | undefined => {
  if (isSalesforceTicketFields(ticketFields)) {
    const foundSalesforceField = ticketFields.output_fields?.find(
      field => field.name === fieldToPredictOutputFieldId,
    );

    if (foundSalesforceField) {
      return {
        label: foundSalesforceField.label,
        value: foundSalesforceField.name,
      };
    }
    return;
  }

  const foundZendeskField = ticketFields?.output_fields?.find(
    (field): field is ZendeskTicketField =>
      'name' in field && String(field.id) === fieldToPredictOutputFieldId,
  );

  if (foundZendeskField) {
    return {
      label: foundZendeskField.name,
      value: foundZendeskField.id.toString(),
    };
  }

  const foundKustomerField = ticketFields?.output_fields?.find(
    (field): field is KustomerTicketField =>
      'title' in field && field.id === fieldToPredictOutputFieldId,
  );

  if (foundKustomerField) {
    return {
      label: foundKustomerField.title || foundKustomerField.helpdesk_field_id,
      value: foundKustomerField.id,
    };
  }

  const foundJiraField = ticketFields?.output_fields?.find(
    (field): field is JiraTicketField =>
      'title' in field && field.id === fieldToPredictOutputFieldId,
  );

  if (foundJiraField) {
    return {
      label: foundJiraField.title || foundJiraField.helpdesk_field_id,
      value: foundJiraField.id,
    };
  }

  const foundFreshdeskField = ticketFields?.output_fields?.find(
    (field): field is FreshdeskTicketField =>
      'title' in field && field.id === fieldToPredictOutputFieldId,
  );

  if (foundFreshdeskField) {
    return {
      label: foundFreshdeskField.title || foundFreshdeskField.helpdesk_field_id,
      value: foundFreshdeskField.id,
    };
  }
};
